import { useEffect, useRef, useState } from 'preact/hooks'
import { sendingDomainsApi } from '@/api/sendingDomains'
import { TERMINAL_DOMAIN_STATUSES, type SendingDomainViewDto } from '@/types/sendingDomains'

// DNS propagation is minutes-to-hours, so poll slowly and stop at a terminal state.
const POLL_INTERVAL_MS = 30_000

interface UseDomainVerificationResult {
  status: SendingDomainViewDto | null
  isLoading: boolean
  error: string | null
  isPolling: boolean
  refresh: () => Promise<void>
}

const isTerminal = (status?: string | null) => !!status && TERMINAL_DOMAIN_STATUSES.includes(status)

/**
 * Polls a sending domain's verification status while `enabled` and the domain is non-terminal.
 * Stops on terminal status, on disable/unmount, and guards in-flight fetches with an active ref
 * so a late response can't setState after close.
 */
export function useDomainVerification(
  clientCompanyId: string,
  domain: SendingDomainViewDto | null,
  enabled: boolean,
): UseDomainVerificationResult {
  const [status, setStatus] = useState<SendingDomainViewDto | null>(domain)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isPolling, setIsPolling] = useState(false)
  const activeRef = useRef(true)
  const domainId = domain?.Id ?? null

  // Keep local status in sync when the selected domain changes (e.g. create -> detail).
  useEffect(() => {
    setStatus(domain)
  }, [domain])

  // Manual refresh for the Refresh button — only invoked from event handlers, never a hook dep.
  const refresh = async () => {
    if (!clientCompanyId || !domainId) return
    setIsLoading(true)
    const res = await sendingDomainsApi.status(clientCompanyId, domainId)
    if (!activeRef.current) return
    setIsLoading(false)
    if (res.error) {
      setError(res.error)
      return
    }
    if (res.data) {
      setError(null)
      setStatus(res.data)
    }
  }

  useEffect(() => {
    activeRef.current = true

    if (!enabled || !domainId || isTerminal(domain?.Status)) {
      setIsPolling(false)
      return () => {
        activeRef.current = false
      }
    }

    let timer: ReturnType<typeof setInterval> | undefined

    const poll = async () => {
      setIsLoading(true)
      const res = await sendingDomainsApi.status(clientCompanyId, domainId)
      if (!activeRef.current) return
      setIsLoading(false)
      if (res.error) {
        setError(res.error)
        return
      }
      if (res.data) {
        setError(null)
        setStatus(res.data)
        if (isTerminal(res.data.Status) && timer) {
          clearInterval(timer)
          timer = undefined
          setIsPolling(false)
        }
      }
    }

    setIsPolling(true)
    poll()
    timer = setInterval(poll, POLL_INTERVAL_MS)

    return () => {
      activeRef.current = false
      setIsPolling(false)
      if (timer) clearInterval(timer)
    }
  }, [clientCompanyId, domainId, enabled, domain?.Status])

  return { status, isLoading, error, isPolling, refresh }
}
